/**
 * UpdateRoleModal Component
 *
 * Modal for updating global role name, description, and permissions.
 * Pre-fills form with existing role data.
 * Uses ResourceScopedPermissionBuilder for user-friendly permission editing.
 *
 * Global Roles Architecture:
 * - Roles are tenant-wide (not application-scoped)
 * - Permissions format: ResourceScopedPermission[] with { actions: string[], resources: string[] }
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Alert } from '../../../components/ui/Alert';
import { ResourceScopedPermissionBuilder } from './ResourceScopedPermissionBuilder';
import { useUpdateRole } from '../hooks/useUpdateRole';
import type { RoleSanitized, ResourceScopedPermission } from '../../../types/role.types';
import { normalizePermissions } from '../../../types/role.types';
import { getErrorMessage } from '../../../lib/utils/errors';

const updateRoleSchema = z.object({
  roleName: z.string().min(1, 'Role name is required').max(100),
  description: z.string().max(500).optional(),
});

type UpdateRoleFormData = z.infer<typeof updateRoleSchema>;

interface UpdateRoleModalProps {
  role: RoleSanitized | null;
  tenantId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const UpdateRoleModal: React.FC<UpdateRoleModalProps> = ({
  role,
  tenantId,
  isOpen,
  onClose,
  onSuccess,
}) => {
  // Use ResourceScopedPermission[] directly
  const [permissions, setPermissions] = useState<ResourceScopedPermission[]>([]);
  const [path, setPath] = useState<string>('/');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<UpdateRoleFormData>({
    resolver: zodResolver(updateRoleSchema),
  });

  const updateRoleMutation = useUpdateRole(tenantId);

  // Initialize form with role data
  useEffect(() => {
    if (role) {
      reset({
        roleName: role.roleName,
        description: role.description,
      });
      setPath(role.path || '/');
      // Normalize permissions to handle both legacy and new format
      const normalizedPermissions = normalizePermissions(role.permissions as any);
      setPermissions(normalizedPermissions);
    }
  }, [role, reset]);

  const onSubmit = async (data: UpdateRoleFormData) => {
    if (!role) return;

    // Validate permissions - must have at least one permission group with actions
    const hasValidPermissions = permissions.some((p) => p.actions.length > 0);
    if (!hasValidPermissions) {
      alert('Please add at least one permission with actions');
      return;
    }

    // Filter out empty permission groups
    const validPermissions = permissions.filter((p) => p.actions.length > 0);

    try {
      await updateRoleMutation.mutateAsync({
        roleId: role.roleId,
        data: {
          roleName: data.roleName,
          description: data.description,
          path: path || '/',
          permissions: validPermissions,
        },
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to update role:', error);
    }
  };

  const handleClose = () => {
    reset();
    updateRoleMutation.reset();
    onClose();
  };

  if (!role) return null;

  // Check if form can be submitted
  const hasValidPermissions = permissions.some((p) => p.actions.length > 0);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>Update Role</Modal.Header>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <div className="space-y-4">
            {/* Error Alert */}
            {updateRoleMutation.isError && (
              <Alert variant="error">
                {getErrorMessage(updateRoleMutation.error)}
              </Alert>
            )}

            {/* Role Code (Read-Only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role Code
              </label>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm text-gray-700 font-mono">
                  {role.roleCode}
                </code>
                <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                  Immutable
                </span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Used in permissions as: {role.roleCode}
              </p>
            </div>

            {/* Role Name */}
            <div>
              <label htmlFor="roleName" className="block text-sm font-medium text-gray-700">
                Role Name <span className="text-red-500">*</span>
              </label>
              <Input
                id="roleName"
                type="text"
                placeholder="e.g., Inventory Manager"
                {...register('roleName')}
                error={errors.roleName?.message}
                className="mt-1"
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <Textarea
                id="description"
                rows={3}
                placeholder="Describe the role's purpose and responsibilities"
                {...register('description')}
                error={errors.description?.message}
                className="mt-1"
              />
            </div>

            {/* Path */}
            <div>
              <label htmlFor="path" className="block text-sm font-medium text-gray-700">
                Path
              </label>
              <Input
                id="path"
                type="text"
                placeholder="e.g., /finance/reports"
                value={path}
                onChange={(e) => setPath(e.target.value)}
                className="mt-1"
              />
              <p className="mt-1 text-xs text-gray-500">
                Organizational path for grouping roles. Use "/" for root or a hierarchy like "/finance/reports".
              </p>
            </div>

            {/* Permissions Builder */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Permissions <span className="text-red-500">*</span>
              </label>
              <ResourceScopedPermissionBuilder
                tenantId={tenantId}
                permissions={permissions}
                onChange={setPermissions}
                roleType={role.type}
              />
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={updateRoleMutation.isPending}
            disabled={!hasValidPermissions}
          >
            Update Role
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
